import { z } from 'zod';

/**
 * Environment variables are validated once at startup so a misconfigured deployment
 * fails immediately with a readable message, rather than surfacing as a confusing
 * network error deep inside a feature.
 */
/**
 * An address, or `null` for "this service is not there". An empty string counts as not there: the Azure release fills in the
 * published bundle's placeholder addresses after the build, and writes an empty one for a service it has not deployed. That
 * has to be decided here, when the value is read, because the build has already folded any earlier check on it away.
 */
const optionalUrl = z.preprocess((value) => (value === '' ? null : value), z.string().url().nullable());

const envSchema = z.object({
  apiBaseUrl: z.string().url(),
  authBaseUrl: z.string().url(),
  /** `null` when this build has no Notifications service: the follow UI is then hidden. */
  notificationsBaseUrl: optionalUrl,
  /** Optional independent Tasks API. Never fall back to a viewer's localhost in production. */
  tasksBaseUrl: optionalUrl,
  authClientId: z.string().min(1),
  authScope: z.string().min(1),
  enableMockApi: z.boolean(),
});

export type AppConfig = z.infer<typeof envSchema>;

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
}

/**
 * The Notifications address, or `null` when there is none.
 *
 * Local development falls back to the compose service on 8100. A production bundle never
 * does: a fallback there would send every viewer's browser to their own `localhost`.
 */
function notificationsBaseUrl(): string | null {
  const value = import.meta.env.VITE_NOTIFICATIONS_BASE_URL;
  if (value !== undefined && value !== '') return value;
  return import.meta.env.DEV ? 'http://localhost:8100' : null;
}

function loadConfig(): AppConfig {
  const raw = {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5043',
    authBaseUrl: import.meta.env.VITE_AUTH_BASE_URL ?? 'http://localhost:5224',
    notificationsBaseUrl: notificationsBaseUrl(),
    tasksBaseUrl: import.meta.env.VITE_TASKS_BASE_URL || null,
    authClientId: import.meta.env.VITE_AUTH_CLIENT_ID ?? 'conbent_web',
    authScope: import.meta.env.VITE_AUTH_SCOPE ?? 'openid profile email api role',
    enableMockApi: parseBoolean(import.meta.env.VITE_ENABLE_MOCK_API, false),
  };

  const result = envSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`Invalid application configuration:\n${issues}`);
  }

  return result.data;
}

export const config: AppConfig = loadConfig();

/**
 * Whether the app should serve itself from in-memory fixtures instead of the .NET
 * services. Enabled by `npm run dev:local`, which sets `VITE_ENABLE_MOCK_API` via
 * `.env.dev-local`.
 *
 * The build-time terms are deliberately first: Vite replaces `import.meta.env.DEV` and
 * `MODE` with literals, so a production build folds this to `false && …` and Rollup drops
 * the mock worker along with everything it imports. Mock code can never reach production
 * by way of a stray environment variable.
 */
export const isMockMode: boolean =
  (import.meta.env.DEV || import.meta.env.MODE === 'dev-local') && config.enableMockApi;

/** Whether this build talks to Conbent.Notifications. Without it, following is hidden. */
export const isNotificationsEnabled: boolean = config.notificationsBaseUrl !== null;
